import { channel } from "redux-saga";
import { call, put, select, take } from "redux-saga/effects";

import actions from "@/actions";
import type Light from "@/models/light/light";
import { Map } from "@/utils";

import handleDuplicateFlags from "../handle-duplicate-flags/handle-duplicate-flags";

const fileChannel = channel();

interface UploadJsonFileAction {
    payload: {
        file: File;
    };
}

/**
 * Reads a JSON file containing lights and imports them into the map.
 *
 * @param {UploadJsonFileAction} action - The action containing the file to upload.
 * @yields Reads the file channel, processes lights, and dispatches import actions.
 */
function* uploadJsonFile({ payload }: UploadJsonFileAction) {
    try {
        const { file } = payload;
        let luminaireSummary: unknown[] = [];

        const onReadFile = ({ target }: ProgressEvent<FileReader>) => {
            if (!target) return;
            const { lights: fileLights } = JSON.parse(target.result as string);
            if (!fileLights) throw Error;
            luminaireSummary = fileLights;
            fileChannel.put(true);
        };

        const reader = new FileReader();
        reader.onload = onReadFile;
        reader.readAsText(file);

        yield take(fileChannel);

        const loadedLights: Record<string, Light> = yield select(
            ({ map }) => map.values.global.present.lights,
        );

        const positioningType: string = yield select(
            ({ map }) =>
                map.general.settings?.fixtureDetails?.positioningType ?? "",
        );

        const lights: Light[] = luminaireSummary.map((l: unknown) =>
            Map.Converter.lights.APIToViewModel(
                l as Parameters<typeof Map.Converter.lights.APIToViewModel>[0],
                positioningType,
            ),
        );

        lights.forEach((light) => {
            const existsInLoaded = Object.values(loadedLights).some(
                (loaded) => loaded.positionId === light.positionId,
            );
            if (!existsInLoaded) {
                light.positionId = 0;
            }
            (light as Light & { changed: boolean }).changed = true;
        });
        yield call(handleDuplicateFlags, lights, false);
    } catch {
        yield put(
            actions.window.showMessage(
                "We couldn't upload this json file. Please try again. If you need assistance, please contact support@atrius-iot.com.",
                "error",
                "advanced-operations",
            ),
        );
    }
}

export default uploadJsonFile;
